import { v4 as uuidv4 } from "uuid";
import { makeAutoObservable, reaction } from "mobx";

class CoachingApplication {
  isSelected = false;
  isSelectedForAnswers = false;

  emailIsWrong = false;
  emailAlreadyInUse = false;
  userAlreadyHasCoach = false;
  error = false;
  seenByCoach = true;

  constructor(
    id = uuidv4(),
    directory = "",
    description = "",
    coach = null,
    client = null,
    date = Date(),
    clientName = "",
    clientSurname = "",
    clientEmail = "",
    hasCompeted = false,
    sections = [],
    coachId = "",
    seenByCoach = true
  ) {
    this.id = id;
    this.directory = directory;
    this.description = description;
    this.coach = coach;
    this.client = client;
    this.date = date;
    this.clientName = clientName;
    this.clientSurname = clientSurname;
    this.clientEmail = clientEmail;
    this.hasCompeted = hasCompeted;
    this.sections = sections;
    this.coachId = coachId;
    this.seenByCoach = seenByCoach;
    makeAutoObservable(this);
  }

  setIsSelected(value) {
    this.isSelected = value;
  }

  setIsSelectedForAnswers(value) {
    this.isSelectedForAnswers = value;
  }

  get numberOfQuestions() {
    let n = 0;
    this.sections.forEach((s) => {
      n += s.questions.length;
    });

    return n;
  }

  get hasQuestions() {
    const hasQuestions = this.sections.some((section) =>
      section.questions.some((question) => question.question !== "")
    );
    return hasQuestions;
  }

  get numberOfQuestionsAsked() {
    const count = this.sections.reduce((total, section) => {
      return (
        total +
        section.questions.filter((question) => question.question !== "").length
      );
    }, 0);
    return count;
  }

  get hasAnswers() {
    const hasAnswers = this.sections.some((section) =>
      section.questions.some((question) => question.answer !== "")
    );
    return hasAnswers;
  }

  get numberOfQuestionsAnswered() {
    const count = this.sections.reduce((total, section) => {
      return (
        total +
        section.questions.filter((question) => question.answer !== "").length
      );
    }, 0);
    return count;
  }

  get requiredQuestionsAreAnswered() {
    return this.sections.every((section) =>
      section.questions
        .filter((question) => question.isRequired)
        .every((question) => question.answer.trim() !== "")
    );
  }

  get canSubmit() {
    return (
      this.requiredQuestionsAreAnswered &&
      this.clientName !== "" &&
      this.clientEmail !== ""
    );
  }

  clearForm() {
    this.clientName = "";
    this.clientSurname = "";
    this.clientEmail = "";
    this.sections.forEach((s) => {
      s.questions.forEach((q) => {
        q.answer = "";
      });
    });
  }

  uploadToCloud() {
    let sectionsArray = this.sections.map((section) => {
      let questionsArray = section.questions.map((question) => ({
        id: question.id.toString().toLowerCase(),
        question: question.question,
        is_required: question.isRequired,
        answer: question.answer,
        order: question.order,
      }));

      return {
        id: section.id.toString().toLowerCase(),
        name: section.name,
        order: section.order,
        questions: questionsArray,
      };
    });

    if (this.coach) {
      //Aqui la que es penja a coaching_applications
      return {
        id: this.id.toString().toLowerCase(),
        directory: this.coach.coachDirectory,
        coach_id: this.coach.id,
        coach_name: this.coach.name,
        coach_surname: this.coach.surname,
        coach_language: this.coach.coachLanguage,
        description: this.description,
        sections: sectionsArray,
      };
    } else {
      //Aqui la que es penja a answered_coaching_applications
      return {
        id: this.id.toString().toLowerCase(),
        // directory: this.directory,
        coach_id: this.coachId,
        date: new Date(),
        client_name: this.clientName,
        client_surname: this.clientSurname,
        client_email: this.clientEmail,
        has_competed: this.hasCompeted,
        sections: sectionsArray,
        seen_by_coach: false,
      };
    }
  }

  fetchInit(formData) {
    this.sections = [];
    this.id = formData.id;
    this.directory = formData.directory;
    this.description = formData.description;
    this.seenByCoach = formData.seen_by_coach ?? true;

    formData.sections.forEach((sectionData) => {
      const newSection = new ApplicationSection(
        sectionData.id,
        sectionData.name,
        sectionData.order,
        [], // Questions will be populated next
        applicationForm // Link the section to the form
      );

      // Handle the questions array within each section
      sectionData.questions.forEach((questionData) => {
        const newQuestion = new ApplicationQuestion(
          questionData.id,
          questionData.question,
          questionData.is_required,
          questionData.answer,
          questionData.order,
          newSection // Link the question to the section
        );

        newSection.questions.push(newQuestion); // Add the question to the section
      });

      this.sections.push(newSection); // Add the section to the form
    });
  }

  setProperty(propertyName, value) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      this[propertyName] = value;
    } else {
      console.error(`Property ${propertyName} does not exist.`);
    }
  }

  getProperty(propertyName) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      return this[propertyName];
    } else {
      console.error(`Property ${propertyName} does not exist.`);
      return undefined;
    }
  }
}

export default CoachingApplication;

export class ApplicationSection {
  constructor(
    id = uuidv4(),
    name = "",
    order = 0,
    questions = [],
    coachingApplication = null
  ) {
    this.id = id;
    this.name = name;
    this.order = order;
    this.questions = questions;
    this.coachingApplication = coachingApplication;
    makeAutoObservable(this);
  }

  addQuestion() {
    const newOrder = this.questions[this.questions.length - 1]
      ? this.questions[this.questions.length - 1].order + 1
      : 0;
    const newQuestion = new ApplicationQuestion(
      uuidv4(),
      "",
      false,
      "",
      newOrder,
      this
    );

    this.questions.push(newQuestion);
  }

  deleteQuestion(question) {
    const index = this.questions.findIndex((q) => q === question);

    if (index > -1 && this.questions.length > 1) {
      this.questions.splice(index, 1);
    }
  }

  setProperty(propertyName, value) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      this[propertyName] = value;
    } else {
      console.error(`Property ${propertyName} does not exist.`);
    }
  }
  getProperty(propertyName) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      return this[propertyName];
    } else {
      console.error(`Property ${propertyName} does not exist.`);
      return undefined;
    }
  }
}

export class ApplicationQuestion {
  constructor(
    id = uuidv4(),
    question = "",
    isRequired = false,
    answer = "",
    order = 0,
    section = null
  ) {
    //Valorar si volem ficar que es puguin dividir per parts els questionaris (mirar com queda sense)
    this.id = id;
    this.question = question;
    this.isRequired = isRequired;
    this.answer = answer;
    this.order = order;
    this.section = section;
    makeAutoObservable(this);
  }

  typeLocalized(t) {
    if (this.isRequired) {
      return t("required-string");
    } else {
      return t("notRequired-string");
    }
  }

  setProperty(propertyName, value) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      this[propertyName] = value;
    } else {
      console.error(`Property ${propertyName} does not exist.`);
    }
  }
  getProperty(propertyName) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      return this[propertyName];
    } else {
      console.error(`Property ${propertyName} does not exist.`);
      return undefined;
    }
  }
}
